import { Box } from '@mui/material';

/**
 * Decorative solar geometry: concentric rotating rings + radiating rays.
 * Pure SVG, no images. Animated via CSS.
 *
 * Il `markerText` è il testo che ruota lungo il cerchio interno (default IT).
 * Va passato dal parent (HomePage) usando i18n: `t('hero.marker_solar')`.
 * Il componente ripete il testo 2 volte per riempire la circonferenza.
 */
export default function SolarGeometry({
  size = 480,
  markerText = 'MARKETPLACE FV  ·  ENERGIA SOLARE  ·  ITALIA  ·  ',
}: {
  size?: number;
  markerText?: string;
}) {
  return (
    <Box
      sx={{
        position: 'relative',
        width: size,
        height: size,
        flexShrink: 0,
      }}
    >
      {/* Outer rotating ring with tick marks */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          animation: 'spinSlow 90s linear infinite',
        }}
      >
        <svg viewBox="0 0 480 480" width="100%" height="100%">
          <circle
            cx="240"
            cy="240"
            r="232"
            fill="none"
            stroke="var(--ink)"
            strokeWidth="0.5"
            strokeDasharray="2 6"
          />
          {Array.from({ length: 60 }).map((_, i) => {
            const angle = (i / 60) * 360;
            const isMajor = i % 5 === 0;
            return (
              <line
                key={i}
                x1="240"
                y1="14"
                x2="240"
                y2={isMajor ? '26' : '20'}
                stroke="var(--ink)"
                strokeWidth={isMajor ? '1' : '0.5'}
                transform={`rotate(${angle} 240 240)`}
              />
            );
          })}
        </svg>
      </Box>

      {/* Mid-ring with sun rays */}
      <Box
        sx={{
          position: 'absolute',
          inset: '12%',
          animation: 'spinReverse 60s linear infinite',
        }}
      >
        <svg viewBox="0 0 360 360" width="100%" height="100%">
          <circle cx="180" cy="180" r="170" fill="none" stroke="var(--sun)" strokeWidth="1" />
          {Array.from({ length: 24 }).map((_, i) => {
            const angle = (i / 24) * 360;
            return (
              <line
                key={i}
                x1="180"
                y1="20"
                x2="180"
                y2="50"
                stroke="var(--sun)"
                strokeWidth="2"
                transform={`rotate(${angle} 180 180)`}
              />
            );
          })}
        </svg>
      </Box>

      {/* Inner sun disk */}
      <Box
        sx={{
          position: 'absolute',
          inset: '30%',
          borderRadius: '50%',
          background: 'radial-gradient(circle at 35% 30%, #E89653 0%, #D4763A 45%, #A85A26 100%)',
          boxShadow: '0 0 80px rgba(212, 118, 58, 0.25)',
        }}
      />

      {/* Central tiny ring with text on it (rotates with outer) */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          animation: 'spinSlow 90s linear infinite',
          opacity: 0.7,
        }}
      >
        <svg viewBox="0 0 480 480" width="100%" height="100%">
          <defs>
            <path
              id="circlePath"
              d="M 240, 240 m -210, 0 a 210,210 0 1,1 420,0 a 210,210 0 1,1 -420,0"
              fill="none"
            />
          </defs>
          <text
            fill="var(--ink-soft)"
            style={{
              fontFamily: '"JetBrains Mono Variable", monospace',
              fontSize: '9px',
              letterSpacing: '4px',
            }}
          >
            <textPath href="#circlePath">
              {`${markerText}${markerText}`}
            </textPath>
          </text>
        </svg>
      </Box>
    </Box>
  );
}
